import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  VIDEO_PRODUCTION_JOB_STATUS,
  VIDEO_PRODUCTION_JOB_STATUS_LABELS,
} from '../../constants.js';
import {
  fetchVideoProductionJob,
  fetchVideoProductionJobs,
  refreshVideoProductionJob,
  downloadVideoProductionFile,
} from '@/features/automation/videoEditing/services/productionJobService.js';
import {
  buildVideoProductionJobsPath,
  VIDEO_PRODUCTION_JOB_ID_PARAM,
} from '../../utils/productionEntryUtils.js';
import PaginationComponent from '@/components/Pagination.js';
import VideoOutputCard from '@/components/media/VideoOutputCard.js';
import './ProductionJobsPage.scss';

const FINAL_STATUSES = new Set([
  VIDEO_PRODUCTION_JOB_STATUS.SUCCESS,
  VIDEO_PRODUCTION_JOB_STATUS.FAILED,
  VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED,
]);

const STATUS_OPTIONS = Object.values(VIDEO_PRODUCTION_JOB_STATUS);
const ITEMS_PER_PAGE = 10;

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
};

const getStatusClassName = (status) => {
  if (status === VIDEO_PRODUCTION_JOB_STATUS.SUCCESS) return 'is-success';
  if (
    status === VIDEO_PRODUCTION_JOB_STATUS.FAILED ||
    status === VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED
  ) {
    return 'is-failed';
  }
  return 'is-running';
};

const getOutputStatusTone = (status) => {
  if (status === VIDEO_PRODUCTION_JOB_STATUS.SUCCESS) return 'success';
  if (
    status === VIDEO_PRODUCTION_JOB_STATUS.FAILED
    || status === VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED
  ) return 'danger';
  return 'progress';
};

const getVideoStandardName = (job) =>
  job?.video_type_title || job?.video_standard || job?.video_type || '-';

const ProductionJobsPage = () => {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get(VIDEO_PRODUCTION_JOB_ID_PARAM) || '';
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState('');
  const [message, setMessage] = useState('');

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const data = await fetchVideoProductionJobs({
        page: currentPage + 1,
        limit: ITEMS_PER_PAGE,
        status: statusFilter || undefined,
      });
      setJobs(data.items);
      setPagination(data.pagination);

      if (jobId) {
        const detail = await fetchVideoProductionJob(jobId);
        setSelectedJob(detail);
      } else {
        setSelectedJob(data.items[0] || null);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || '获取阿里云视频任务失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, jobId, statusFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const timer = setInterval(loadJobs, 20000);
    return () => clearInterval(timer);
  }, [loadJobs]);

  const activeJob = useMemo(() => {
    if (selectedJob) return selectedJob;
    return jobs.find((job) => job.id === jobId) || jobs[0] || null;
  }, [jobId, jobs, selectedJob]);

  const handleRefresh = async (job) => {
    if (!job?.id) return;
    setRefreshingId(job.id);
    setMessage('');
    try {
      const updated = await refreshVideoProductionJob(job.id);
      setJobs((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setSelectedJob(updated);
    } catch (error) {
      setMessage(error.response?.data?.message || '刷新任务状态失败');
    } finally {
      setRefreshingId('');
    }
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <div className="video-production-jobs-page">
      <div className="video-production-jobs-page__header">
        <div>
          <h2>阿里云视频任务</h2>
          <p>任务结果会同步更新订单工作流状态，订单完成仍由管理员确认。</p>
        </div>
        <div className="video-production-jobs-page__toolbar">
          <Form.Select
            size="sm"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCurrentPage(0);
            }}
          >
            <option value="">全部状态</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {VIDEO_PRODUCTION_JOB_STATUS_LABELS[status] || status}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {message && <div className="video-production-jobs-page__message">{message}</div>}

      <div className="video-production-jobs-page__content">
        <div className="video-production-jobs-page__table-panel">
          <div className="video-production-jobs-page__table">
            <Table striped bordered hover responsive className="jobs-table">
              <thead>
                <tr>
                  <th>客户姓名</th>
                  <th>模板</th>
                  <th>状态</th>
                  <th>提交人</th>
                  <th>提交时间</th>
                  <th>完成时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className={activeJob?.id === job.id ? 'is-selected' : ''}
                    onClick={() => {
                      setSelectedJob(job);
                      navigate(buildVideoProductionJobsPath(job.id));
                    }}
                  >
                    <td>{job.client_name || job.client_id}</td>
                    <td>{job.template_name || job.video_template_id}</td>
                    <td>
                      <span className={`status-pill ${getStatusClassName(job.status)}`}>
                        {VIDEO_PRODUCTION_JOB_STATUS_LABELS[job.status] || job.status}
                      </span>
                    </td>
                    <td>{job.submitted_by_name || job.submitted_by || '-'}</td>
                    <td>{formatTime(job.submitted_at || job.create_time)}</td>
                    <td>{formatTime(job.finished_at)}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={
                          FINAL_STATUSES.has(job.status) ||
                          refreshingId === job.id
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRefresh(job);
                        }}
                      >
                        {refreshingId === job.id ? '同步中' : '同步状态'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!loading && jobs.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center">
                      暂无阿里云视频任务
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="7" className="text-center">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="video-production-jobs-page__pagination">
            <span>共 {pagination.total} 条</span>
            <PaginationComponent
              pageCount={pagination.totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>

        <aside className="video-production-jobs-page__detail">
          <h3>任务详情</h3>
          {activeJob ? (
            <>
              <div className="detail-line">
                <span>任务 ID</span>
                <strong>{activeJob.id}</strong>
              </div>
              <div className="detail-line">
                <span>客户姓名</span>
                <strong>{activeJob.client_name || activeJob.client_id}</strong>
              </div>
              <div className="detail-line">
                <span>视频标准</span>
                <strong>{getVideoStandardName(activeJob)}</strong>
              </div>
              <div className="detail-line">
                <span>状态</span>
                <strong>
                  {VIDEO_PRODUCTION_JOB_STATUS_LABELS[activeJob.status] ||
                    activeJob.status}
                </strong>
              </div>
              <div className="detail-media">
                <span>输出视频</span>
                {activeJob.output_url ? (
                  <VideoOutputCard
                    errorMessage={activeJob.error_message}
                    isFailed={
                      activeJob.status === VIDEO_PRODUCTION_JOB_STATUS.FAILED
                      || activeJob.status === VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED
                    }
                    isSuccess={
                      activeJob.status === VIDEO_PRODUCTION_JOB_STATUS.SUCCESS
                    }
                    name={activeJob.id}
                    onActionError={(error, fallback) =>
                      setMessage(error.response?.data?.message || error.message || fallback)}
                    onDownload={() => downloadVideoProductionFile(activeJob.id)}
                    showMeta={false}
                    statusLabel={
                      VIDEO_PRODUCTION_JOB_STATUS_LABELS[activeJob.status]
                      || activeJob.status
                    }
                    statusTone={getOutputStatusTone(activeJob.status)}
                    videoUrl={activeJob.output_url}
                  />
                ) : (
                  <strong>-</strong>
                )}
              </div>
              {activeJob.error_message && (
                <div className="detail-error">
                  <span>错误信息</span>
                  <p>{activeJob.error_message}</p>
                </div>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(`/review/${activeJob.client_id}`)}
              >
                查看订单
              </Button>
            </>
          ) : (
            <div className="video-production-jobs-page__empty">
              请选择一个任务
            </div>
          )}
        </aside>
      </div>

    </div>
  );
};

export default ProductionJobsPage;
